// Scheduled payments: plan a supplier payment for a future date, then record
// the due ones after the money has moved outside Accounting.
//
// Plans are persisted in the books. A plan is Pending ("held") until it is
// processed; processing writes the payment record and its journal and never
// sends money. Amounts are whole currency units in the form, converted to
// integer minor units (cents) once, at create.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useRouterState } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { BillPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  createScheduledPayment,
  listScheduledPayments,
  processScheduledPayments,
  type ScheduledPaymentRecord,
} from "@/lib/api";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDay(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

function scheduleStatus(schedule: ScheduledPaymentRecord) {
  switch (schedule.status) {
    case "completed":
      return (
        <StatusDot
          tone="success"
          label={
            schedule.processed_at
              ? `Processed ${formatDay(schedule.processed_at)}`
              : "Processed"
          }
        />
      );
    case "processing":
      return <StatusDot tone="warning" label="Processing" />;
    case "failed":
      return <StatusDot tone="danger" label="Failed" />;
    case "cancelled":
      return <StatusDot tone="idle" label="Cancelled" />;
    default:
      return <StatusDot tone="idle" label="Held" />;
  }
}

export function ScheduledPaymentsPage({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  const sourceBill = useRouterState({
    select: (state) => {
      const bill = (state.location.search as Record<string, unknown>).bill;
      return typeof bill === "string" ? bill : "";
    },
  });
  const [billId, setBillId] = useState("");
  const [supplier, setSupplier] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayInput);
  const [notes, setNotes] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [scheduled, setScheduled] = useState<ScheduledPaymentRecord[] | null>(
    null,
  );
  const [scheduledError, setScheduledError] = useState<string | null>(null);

  const reloadScheduled = useCallback(() => {
    setScheduledError(null);
    listScheduledPayments()
      .then(setScheduled)
      .catch((cause: unknown) => setScheduledError(toErrorMessage(cause)));
  }, []);

  useEffect(() => {
    reloadScheduled();
  }, [reloadScheduled]);

  const [asOf, setAsOf] = useState(todayInput);
  const [processing, setProcessing] = useState(false);
  const [paidExternally, setPaidExternally] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [retry, setRetry] = useState<{ run: () => void } | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!sourceBill) return;
    setBillId(sourceBill);
    setDrawerOpen(true);
  }, [sourceBill]);

  const openCreator = () => {
    setCreateError(null);
    setDrawerOpen(true);
  };

  const submitCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const minor = toMinor(amount);
    if (!billId) {
      setCreateError("Pick the bill this payment covers.");
      return;
    }
    if (!supplier.trim()) {
      setCreateError("Name the supplier.");
      return;
    }
    if (minor === null) {
      setCreateError("Enter an amount above zero.");
      return;
    }
    if (!date) {
      setCreateError("Pick the date to pay on.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createScheduledPayment({
        bill_id: billId,
        supplier_name: supplier.trim(),
        amount_cents: minor,
        scheduled_date: date,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      if (!result?.id) {
        setCreateError(
          "The payment was planned but the API returned no reference, so it cannot be listed here.",
        );
        return;
      }
      setDrawerOpen(false);
      reloadScheduled();
      setNotice(
        `Planned a ${supplier.trim()} payment record for ${date}. It stays held until you record due payments after paying the supplier outside Accounting.`,
      );
      setBillId("");
      setSupplier("");
      setAmount("");
      setNotes("");
    } catch (cause) {
      setCreateError(toErrorMessage(cause));
    } finally {
      setCreating(false);
    }
  };

  const performProcess = async () => {
    if (!paidExternally) {
      setError(
        "Confirm the due payments have already been made outside Accounting.",
      );
      setRetry(null);
      setConfirmOpen(false);
      return;
    }
    if (!asOf) {
      setError("Pick the date to process as of.");
      setRetry(null);
      return;
    }
    setProcessing(true);
    setError(null);
    setRetry(null);
    setNotice(null);
    try {
      const result = await processScheduledPayments({ as_of: asOf });
      const count = result?.processed ?? 0;
      const stillHeld = (scheduled ?? []).filter(
        (item) => item.status === "pending" && item.scheduled_date > asOf,
      ).length;
      reloadScheduled();
      setConfirmOpen(false);
      setPaidExternally(false);
      setNotice(
        `Recorded ${count} scheduled payment${count === 1 ? "" : "s"} due on or before ${asOf}.` +
          (stillHeld > 0
            ? ` ${stillHeld} payment${stillHeld === 1 ? " is" : "s are"} still held because the due date is after ${asOf}.`
            : "") +
          " No money was sent.",
      );
    } catch (cause) {
      setError(toErrorMessage(cause));
      setRetry({
        run: () => {
          setPaidExternally(true);
          setConfirmOpen(true);
        },
      });
      setConfirmOpen(false);
    } finally {
      setProcessing(false);
    }
  };

  const submitProcess = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!paidExternally) {
      setError(
        "Confirm the due payments have already been made outside Accounting.",
      );
      setRetry(null);
      return;
    }
    if (!asOf) {
      setError("Pick the date to process as of.");
      setRetry(null);
      return;
    }
    if (confirmOpen) {
      void performProcess();
      return;
    }
    setConfirmOpen(true);
  };

  const actions = (
    <div className="flex flex-wrap gap-2">
      <Button type="button" onClick={openCreator}>
        Plan a payment
      </Button>
      {embedded ? null : (
        <Button asChild variant="secondary">
          <Link
            to="/payments"
            search={(previous) => ({ ...previous, tab: "scheduled" })}
          >
            Back to payments
          </Link>
        </Button>
      )}
    </div>
  );

  return (
    <div className={embedded ? "grid gap-4" : "grid gap-6"}>
      {embedded ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : (
        <PageHeader
          title="Scheduled payments"
          description="Schedule supplier payment records, then record payments made outside Accounting. This does not send money."
          actions={actions}
        />
      )}

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          {retry ? (
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={retry.run}
              >
                Retry
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
          {notice}
        </output>
      ) : null}

      <section aria-label="Planned payments" className="grid gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">
            Planned payments
          </h2>
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">
            Held means the payment is only a plan — not paid and not recorded in
            the books yet.
          </p>
        </div>

        {scheduledError ? (
          <div
            role="alert"
            className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
          >
            {scheduledError}
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={reloadScheduled}
              >
                Retry
              </Button>
            </div>
          </div>
        ) : scheduled === null ? (
          <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
        ) : scheduled.length === 0 ? (
          <EmptyState
            title="No payments planned"
            reason="Plan a supplier payment to keep track of it here. A plan stays held until you record due payments after paying the supplier outside Accounting."
            action={
              <Button type="button" onClick={openCreator}>
                Plan the first payment
              </Button>
            }
          />
        ) : (
          <>
            <div
              className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
              data-testid="scheduled-payments-table"
            >
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduled.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.supplier_name}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDay(item.scheduled_date)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(item.amount_cents)}
                        </TableCell>
                        <TableCell>{scheduleStatus(item)}</TableCell>
                        <TableCell className="text-xs text-[var(--muted-foreground)]">
                          {item.notes ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="scheduled-payments-stack"
            >
              {scheduled.map((item) => (
                <article
                  key={item.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title">{item.supplier_name}</span>
                    <span className="font-medium tabular-nums">
                      {money(item.amount_cents)}
                    </span>
                  </div>
                  <CardField label="Due">
                    {formatDay(item.scheduled_date)}
                  </CardField>
                  <CardField label="Status">{scheduleStatus(item)}</CardField>
                  <CardField label="Notes">{item.notes ?? "—"}</CardField>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      <section aria-label="Run due payments" className="grid gap-3">
        <h2 className="text-lg font-semibold tracking-tight">
          Run due payments
        </h2>
        <form
          onSubmit={submitProcess}
          className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
        >
          <label htmlFor="sp-asof" className="grid gap-1 text-xs font-medium">
            Process everything due as of
            <Input
              id="sp-asof"
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              disabled={processing}
            />
          </label>
          <Button type="submit" disabled={processing || !asOf}>
            {processing ? "Recording…" : "Record due payments"}
          </Button>
          <p className="w-full text-xs text-[var(--muted-foreground)]">
            This records every due payment on or before the chosen date. It
            does not send money or verify a bank transfer. Payments dated
            after the chosen date stay held.
          </p>
          <label className="flex w-full items-start gap-2 text-sm">
            <input
              type="checkbox"
              required
              checked={paidExternally}
              disabled={processing}
              onChange={(event) => {
                const checked = event.target.checked;
                setPaidExternally(checked);
                setConfirmOpen(checked);
              }}
            />
            I confirm all due payments have already been made outside
            Accounting.
          </label>
        </form>
      </section>

      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title="Plan a supplier payment"
        description="This records a plan only — no money moves. Recording the plan in the books happens later, after you pay the supplier outside Accounting."
        className="w-[min(34rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDrawerOpen(false)}
              disabled={creating}
            >
              Back to payments
            </Button>
            <Button
              type="submit"
              form="sp-create-form"
              disabled={creating}
              className="ms-auto"
            >
              {creating ? "Planning…" : "Plan payment"}
            </Button>
          </>
        }
      >
        <form
          id="sp-create-form"
          onSubmit={submitCreate}
          className="grid gap-3"
          aria-label="Plan a payment"
        >
          {createError ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {createError}
            </div>
          ) : null}
          <label htmlFor="sp-bill" className="grid gap-1 text-xs font-medium">
            Bill
            <BillPicker
              id="sp-bill"
              value={billId}
              onChange={setBillId}
              disabled={creating}
              required
            />
          </label>
          <label
            htmlFor="sp-supplier"
            className="grid gap-1 text-xs font-medium"
          >
            Supplier
            <Input
              id="sp-supplier"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="Acme Supplies"
              disabled={creating}
            />
          </label>
          <label htmlFor="sp-amount" className="grid gap-1 text-xs font-medium">
            Amount ({DISPLAY_CURRENCY})
            <Input
              id="sp-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              disabled={creating}
            />
          </label>
          <label htmlFor="sp-date" className="grid gap-1 text-xs font-medium">
            Pay on
            <Input
              id="sp-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={creating}
            />
          </label>
          <label htmlFor="sp-notes" className="grid gap-1 text-xs font-medium">
            Notes (optional)
            <Input
              id="sp-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Pay after the delivery lands"
              disabled={creating}
            />
          </label>
        </form>
      </Drawer>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmOpen(false);
            setPaidExternally(false);
          }
        }}
        title="Record due payments?"
        description={`This records every scheduled payment due on or before ${asOf} in the books. It does not send money or verify a bank transfer — only continue if the supplier payments were made outside Accounting.`}
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setConfirmOpen(false);
              setPaidExternally(false);
            }}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void performProcess()}
            disabled={processing || !asOf}
          >
            {processing ? "Recording…" : "Record due payments"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
